import { ReactElement, useMemo } from 'react';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { DataMode, buildRatioTimeSeries } from './auxSensorChannels';
import {
  AUX_COLORS, DEFAULT_PAIR_LABEL, RATIO_ONE_ANNOTATION, auxBaseOptions, ratioYAxis, timeAxisLabel,
} from './auxPlotStyle';
import AuxPlotCard from './AuxPlotCard';

interface AuxRatioTimePlotProps {
  gridded: Record<string, (number | null)[]> | undefined;
  circumference: number;
  /** Min linear speed (m/s) — samples below are masked (shared slider). */
  threshold: number;
  dataMode?: DataMode;
  isLoading?: boolean;
  /**
   * Names the two wheel sensors the ratio is between, e.g.
   * '46145 (Front) / 6721 (Rear)'. Absent — a run whose sensor records
   * cannot identify them — it falls back to the positional wording.
   */
  pairLabel?: string;
}


const CHART_HEIGHT = 320;

/**
 * Slip ratio (wheel speed 1 / wheel speed 2) over time. Samples below the shared
 * min-speed threshold are drawn in grey rather than hidden, so it stays obvious
 * how much of the run the threshold is excluding. A dashed reference line marks
 * ratio = 1 (sensor agreement).
 */
export default function AuxRatioTimePlot({
  gridded,
  circumference,
  threshold,
  dataMode = 'all',
  isLoading = false,
  pairLabel = DEFAULT_PAIR_LABEL,
}: AuxRatioTimePlotProps): ReactElement {
  const { included, excluded } = useMemo(
    () => buildRatioTimeSeries(gridded, circumference, threshold, dataMode),
    [gridded, circumference, threshold, dataMode],
  );

  const hasData = useMemo(
    () => included.some((p) => p[1] != null) || excluded.some((p) => p[1] != null),
    [included, excluded],
  );

  const chartSeries = useMemo(
    () => [
      { name: pairLabel, data: included },
      { name: `Below ${threshold.toFixed(1)} m/s`, data: excluded },
    ],
    [included, excluded, threshold, pairLabel],
  );

  const options = useMemo<ApexOptions>(() => {
    const base = auxBaseOptions(CHART_HEIGHT);
    return {
      ...base,
      chart: { ...base.chart, type: 'line' },
      colors: [AUX_COLORS.ratio, AUX_COLORS.masked],
      stroke: { curve: 'straight', width: 1.5 },
      markers: { size: 0 },
      xaxis: {
        type: 'numeric',
        title: { text: 'Time (seconds)', style: { fontSize: '13px', fontWeight: 600 } },
        // No `decimalsInFloat: 0` — it is deliberately gone, and its absence is
        // load-bearing rather than an oversight of the move to `timeAxisLabel`.
        // Apex rounds generated tick VALUES to it, so pinning it at 0 holds the
        // axis to whole seconds and a sub-second zoom lands several ticks on the
        // same value. See `xAxisLabel`.
        labels: { formatter: timeAxisLabel },
      },
      yaxis: ratioYAxis(pairLabel),
      annotations: RATIO_ONE_ANNOTATION,
      tooltip: {
        x: { formatter: (value: number) => `${value.toFixed(1)} s` },
        y: { formatter: (value: number) => (value != null ? value.toFixed(3) : '') },
      },
    };
  }, [pairLabel]);

  return (
    <AuxPlotCard
      title="Slip Ratio vs Time"
      isLoading={isLoading}
      isEmpty={!hasData}
      emptyMessage="No slip-ratio samples for this run (needs two wheel sensors)."
    >
      <ReactApexChart options={options} series={chartSeries} type="line" height={CHART_HEIGHT} />
    </AuxPlotCard>
  );
}
